import snakeCase from 'lodash/snakeCase';

/**
 * Converts all keys in an object to snake_case. Useful for analytics clients that use snakeCase (like GA).
 *
 * e.g. {fruitName: 'apple', fruitDetails: { retailLocations: [{ shopName: 'Fruiterie', ownerName: 'Sam' }] }}
 *  -> {fruit_name: 'apple', fruit_details: { retail_locations: [{ shop_name: 'Fruiterie', owner_name: 'Sam' }] }}
 *
 * @recursive
 */

type ConversionTypes = Array<unknown> | Record<string, unknown> | unknown;
type ConversionReturn<T> = T extends Record<string, unknown> ? Record<string, unknown> : T;

export function checkConvertKeysToSnakeCase<T extends ConversionTypes>(obj: T): ConversionReturn<T> {
  if (obj instanceof Array) {
    // man i hate `as` but this is all `unknown` values so its kind of a whatever
    return obj.map((item) => checkConvertKeysToSnakeCase(item)) as ConversionReturn<T>;
  }

  if (isObject(obj)) {
    return convertKeysToSnakeCase(obj) as ConversionReturn<T>;
  }

  // handling the unknown part
  return obj as ConversionReturn<T>;
}

export function convertKeysToSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [snakeCase(k), checkConvertKeysToSnakeCase(v)]));
}

// return true if it's a normal key:value object
function isObject(obj: ConversionTypes): obj is Record<string, unknown> {
  return (
    typeof obj === 'object' &&
    // because `typeof null` is 'object'
    obj !== null
  );
}
